import React, { useState } from "react";
import { useApp } from "@/context/AppProvider";
import { THEME_PRESETS } from "@/content";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Lock, Pencil, Eye, Save, Palette, ListOrdered, LogOut, RotateCcw, ArrowUp, ArrowDown, Check } from "lucide-react";

const inputStyle =
  "bg-transparent border-0 border-b rounded-none px-0 h-11 focus-visible:ring-0 focus-visible:border-b-2 shadow-none";

export const EditorToolbar = () => {
  const {
    user, login, logout, ed, lang,
    editMode, setEditMode, dirty, saving, save, resetContent,
    theme, updateTheme, sectionOrder, moveSection,
  } = useApp();

  const [loginOpen, setLoginOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      await login(email, password);
      setLoginOpen(false);
      setEmail("");
      setPassword("");
      setEditMode(true);
      toast.success(lang === "ar" ? "مرحباً بعودتك" : "Welcome back");
    } catch {
      toast.error(lang === "ar" ? "بيانات غير صحيحة." : "Invalid credentials.");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleSave = async () => {
    // Commit any in-progress inline edit (contentEditable commits on blur)
    if (document.activeElement && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
    }
    await new Promise((r) => setTimeout(r, 80));
    try {
      await save();
      toast.success(ed.saved);
    } catch {
      toast.error(lang === "ar" ? "فشل الحفظ" : "Save failed");
    }
  };

  const handleReset = async () => {
    if (window.confirm(ed.resetConfirm)) {
      await resetContent();
      toast.success(ed.saved);
    }
  };

  const pill = "inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-body font-medium transition-colors";

  return (
    <>
      {/* Logged-out: subtle lock button */}
      {!user && (
        <button
          data-testid="owner-access-btn"
          onClick={() => setLoginOpen(true)}
          className="fixed bottom-5 end-5 z-[70] w-11 h-11 rounded-full glass shadow-soft flex items-center justify-center hover:scale-105 transition-transform"
          style={{ color: "var(--ink-soft)" }}
          title={ed.ownerAccess}
          aria-label={ed.ownerAccess}
        >
          <Lock size={16} />
        </button>
      )}

      {/* Logged-in: floating toolbar */}
      {user && (
        <div
          data-testid="editor-toolbar"
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-2 rounded-full p-2 glass shadow-lift"
          dir="ltr"
        >
          {!editMode ? (
            <button data-testid="enter-edit-btn" onClick={() => setEditMode(true)} className={pill} style={{ backgroundColor: "var(--ink)", color: "#fff" }}>
              <Pencil size={15} /> {ed.edit}
            </button>
          ) : (
            <>
              <button data-testid="preview-btn" onClick={() => setEditMode(false)} className={pill} style={{ color: "var(--ink)" }} title={ed.preview}>
                <Eye size={15} /> <span className="hidden sm:inline">{ed.preview}</span>
              </button>

              {/* Design */}
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="design-btn" className={pill} style={{ color: "var(--ink)" }} title={ed.design}>
                    <Palette size={15} /> <span className="hidden sm:inline">{ed.design}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent side="top" className="w-80 rounded-2xl" data-testid="design-panel">
                  <div className="space-y-5">
                    <div>
                      <p className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--ink-soft)" }}>{ed.presets}</p>
                      <div className="flex flex-wrap gap-2">
                        {THEME_PRESETS.map((p) => (
                          <button
                            key={p.name}
                            data-testid={`preset-${p.name}`}
                            onClick={() => updateTheme(p)}
                            className="w-8 h-8 rounded-full border transition-transform hover:scale-110 relative"
                            style={{ background: p.accent, borderColor: "rgba(0,0,0,0.1)" }}
                            title={p.name}
                          >
                            {theme.accent === p.accent && <Check size={14} className="absolute inset-0 m-auto text-white" />}
                          </button>
                        ))}
                      </div>
                    </div>
                    <ColorRow label={ed.accent} value={theme.accent} onChange={(v) => updateTheme({ accent: v, accentBright: v })} testid="accent-color" />
                    <ColorRow label={ed.inkColor} value={theme.ink} onChange={(v) => updateTheme({ ink: v })} testid="ink-color" />
                    <ColorRow label={ed.bgColor} value={theme.bg} onChange={(v) => updateTheme({ bg: v })} testid="bg-color" />
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm" style={{ color: "var(--ink)" }}>{ed.textSize}</span>
                        <span className="text-xs" style={{ color: "var(--ink-soft)" }}>{Math.round((theme.textScale || 1) * 100)}%</span>
                      </div>
                      <Slider
                        data-testid="text-size-slider"
                        min={85}
                        max={120}
                        step={1}
                        value={[Math.round((theme.textScale || 1) * 100)]}
                        onValueChange={(v) => updateTheme({ textScale: v[0] / 100 })}
                      />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {/* Sections */}
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="sections-btn" className={pill} style={{ color: "var(--ink)" }} title={ed.sections}>
                    <ListOrdered size={15} /> <span className="hidden sm:inline">{ed.sections}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent side="top" className="w-72 rounded-2xl" data-testid="sections-panel">
                  <p className="text-xs uppercase tracking-widest mb-3" style={{ color: "var(--ink-soft)" }}>{ed.sections}</p>
                  <div className="space-y-1.5">
                    {sectionOrder.map((key, i) => (
                      <div key={key} className="flex items-center justify-between rounded-lg px-3 py-2" style={{ backgroundColor: "var(--sand)" }}>
                        <span className="text-sm" style={{ color: "var(--ink)" }}>{ed.sectionNames[key] || key}</span>
                        <div className="flex items-center gap-1">
                          <button onClick={() => moveSection(i, -1)} disabled={i === 0} className="ec-btn" data-testid={`section-up-${key}`}><ArrowUp size={13} /></button>
                          <button onClick={() => moveSection(i, 1)} disabled={i === sectionOrder.length - 1} className="ec-btn" data-testid={`section-down-${key}`}><ArrowDown size={13} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              <button data-testid="save-btn" onClick={handleSave} disabled={saving} className={pill} style={{ backgroundColor: dirty ? "var(--gold)" : "var(--ink)", color: "#fff" }}>
                <Save size={15} /> {saving ? ed.saving : ed.save}
              </button>

              <button data-testid="reset-btn" onClick={handleReset} className="ec-btn" style={{ color: "var(--ink-soft)" }} title={ed.reset}>
                <RotateCcw size={15} />
              </button>
            </>
          )}

          <button data-testid="logout-btn" onClick={logout} className="ec-btn" style={{ color: "var(--ink-soft)" }} title={ed.logout}>
            <LogOut size={15} />
          </button>
        </div>
      )}

      {/* Hint banner in edit mode */}
      {user && editMode && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[65] glass rounded-full px-5 py-2 shadow-soft text-xs" style={{ color: "var(--ink-soft)" }} data-testid="edit-hint">
          {ed.hint}
        </div>
      )}

      {/* Login dialog */}
      <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
        <DialogContent className="rounded-2xl" data-testid="login-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{lang === "ar" ? "تسجيل دخول المالك" : "Owner sign in"}</DialogTitle>
            <DialogDescription>{lang === "ar" ? "المالك فقط يمكنه تحرير الموقع." : "Only the site owner can edit this site."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLogin} className="space-y-5 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>{lang === "ar" ? "البريد الإلكتروني" : "Email"}</Label>
              <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputStyle} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>{lang === "ar" ? "كلمة المرور" : "Password"}</Label>
              <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputStyle} />
            </div>
            <button data-testid="login-submit" type="submit" disabled={loggingIn} className="w-full rounded-full py-3.5 text-white text-sm font-body font-medium disabled:opacity-60" style={{ backgroundColor: "var(--ink)" }}>
              {loggingIn ? (lang === "ar" ? "جارٍ الدخول…" : "Signing in…") : (lang === "ar" ? "تسجيل الدخول" : "Sign in")}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

const ColorRow = ({ label, value, onChange, testid }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm" style={{ color: "var(--ink)" }}>{label}</span>
    <div className="flex items-center gap-2">
      <span className="text-xs uppercase" style={{ color: "var(--ink-soft)" }}>{value}</span>
      <input
        type="color"
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-8 h-8 rounded-lg cursor-pointer border"
        style={{ borderColor: "rgba(0,0,0,0.1)" }}
      />
    </div>
  </div>
);
